import { ELanguage, getELoanPurposeList, getEProvinceList } from '@if/core/types'
import { ControlledPhoneField, ControlledSelect, ControlledTextField, useTranslatedOptionEnum } from '@if/mui'
import { Grid, MenuItem, Typography } from '@mui/material'
import { BannerDto } from '@src/data/types/BannerSchema'
import { JSX, memo, useMemo } from 'react'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  control: Control<BannerDto>
}

const BannerInformationPanel = ({ control }: Props) => {
  const { t } = useTranslation()
  const loanPurposeOptions = useTranslatedOptionEnum('ELoanPurpose', getELoanPurposeList(), 'enum')
  const provinceOptions = useTranslatedOptionEnum('EProvince', getEProvinceList(), 'enum')
  const languageOptions = useMemo<JSX.Element[]>(
    () => [
      <MenuItem key="0" value="0">
        {t(`ELanguage.${ELanguage.French}`, { ns: 'enum' })}
      </MenuItem>,
      <MenuItem key="1" value="1">
        {t(`ELanguage.${ELanguage.English}`, { ns: 'enum' })}
      </MenuItem>,
    ],
    [t],
  )

  return (
    <>
      <Typography variant="h6">{t('banner.bannerInformation')}</Typography>
      <Grid container spacing={2} alignItems="flex-start">
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ControlledTextField id="name" name="name" control={control} label={t('common.name')} fullWidth />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ControlledSelect
            id="serviceCategory"
            name="serviceCategory"
            control={control}
            label={t('common.serviceCategory')}
            options={loanPurposeOptions}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ControlledTextField
            id="email"
            name="email"
            type="email"
            control={control}
            label={t('common.email')}
            fullWidth
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ControlledPhoneField id="phone" name="phone" control={control} label={t('common.phone')} fullWidth />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ControlledTextField id="website" name="website" control={control} label={t('common.website')} fullWidth />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 6,
          }}
        >
          <ControlledSelect
            id="languageId"
            name="languageId"
            control={control}
            label={t('common.language')}
            options={languageOptions}
            fullWidth
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 12,
          }}
        >
          <ControlledTextField
            id="address"
            name="address"
            control={control}
            label={t('common.address')}
            fullWidth
            slotProps={{
              htmlInput: { maxLength: 100 },
            }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ControlledTextField
            id="city"
            name="city"
            control={control}
            label={t('common.city')}
            fullWidth
            slotProps={{
              htmlInput: { maxLength: 50 },
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ControlledSelect
            id="province"
            name="province"
            control={control}
            label={t('common.province')}
            fullWidth
            options={provinceOptions}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 4,
          }}
        >
          <ControlledTextField id="zipCode" name="zipCode" control={control} label={t('common.postalCode')} fullWidth />
        </Grid>
      </Grid>
    </>
  )
}
export default memo(BannerInformationPanel)
